import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ActionsMenu from "./ActionsMenu";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";

export default function Regulations() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allRows, setAllRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [collegeName, setCollegeName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;
  const [form, setForm] = useState({
    college_id: "",
    regulation_code: "",
    regulation_name: "",
    description: "",
    status: "ACTIVE",
    created_by: "",
  });

  // ✅ Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ✅ Fetch Data
  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/regulations/${clientId}/${collegeId}`);
      if (data.success) {
        const mappedRows = data.items.map((r) => ({
          id: r.regulation_id,
          ...r,
          college_name: collegeName || "Unknown",
          created_on: r.created_on
            ? new Date(r.created_on).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
        }));
        setAllRows(mappedRows);
        setRows(mappedRows);
        setRowCount(mappedRows.length);
      } else {
        showSnackbar(data.message || "Failed to load data", "error");
      }
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => {
        if (res.data.success) {
          setCollegeName(res.data.data.college_name);
        }
      })
      .catch((err) => console.error(err));
  }, [collegeId]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (collegeName) {
      fetchData();
    }
  }, [collegeName]);
  // ✅ Local Search filter
  useEffect(() => {
    if (!search) {
      setRows(allRows);
    } else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) =>
            r.regulation_code?.toLowerCase().includes(s) ||
            r.regulation_name?.toLowerCase().includes(s) ||
            r.description?.toLowerCase().includes(s) ||
            r.created_by?.toLowerCase().includes(s)
        )
      );
    }
  }, [search, allRows]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page, pageSize, search]);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      college_id: "",
      regulation_code: "",
      regulation_name: "",
      description: "",
      status: "ACTIVE",
      created_by: "",
    });
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditing(row);
    setForm({
      college_id: row.college_id,
      regulation_code: row.regulation_code,
      regulation_name: row.regulation_name,
      description: row.description || "",
      status: row.status,
      created_by: row.created_by,
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this regulation?")) return;
    let regulationId = row.regulation_id;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/regulations/${clientId}/${collegeId}/${regulationId}`
      );
      fetchData();
      showSnackbar("Regulation deleted successfully", "success");
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Delete failed", "error");
    }
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      college_id: Number(collegeId),
      client_id: Number(clientId),
      created_by: user_email,
    };
    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/regulations/${clientId}/${collegeId}/${editing.id}`,
          payload
        );
        showSnackbar("Regulation updated successfully", "success");
      } else {
        await axios.post(`${API_BASE_URL}/api/regulations/${clientId}/${collegeId}`, payload);
        showSnackbar("Regulation created successfully", "success");
      }
      setOpen(false);
      fetchData();
    } catch (e) {
      console.error(e);
      showSnackbar(e?.response?.data?.message || "Save failed", "error");
    }
  };

  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/regulations/${clientId}/${collegeId}/upload/csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  function initCap(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  const columns = useMemo(
    () => [
      {
        field: "regulation_id",
        headerName: "ID",
        flex: 0.5,
        minWidth: 50,
        valueGetter: (p) => p.row.id,
      },
      {
        field: "college_name",
        headerName: "College Name",
        flex: 2,
        minWidth: 150,
      },
      {
        field: "regulation_code",
        headerName: "Code",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "regulation_name",
        headerName: "Name",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "created_on",
        headerName: "Created On",
        flex: 1.2,
        minWidth: 120,
        valueGetter: (p) => p.row.created_on?.slice(0, 10),
      },
      {
        field: "created_by",
        headerName: "Created By",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => (
          <span style={{ color: params.value === "ACTIVE" ? "green" : "red" }}>{params.value}</span>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <ActionsMenu row={params.row} onEdit={handleOpenEdit} onDelete={handleDelete} />
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          {" "}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {" "}
            REGULATIONS{" "}
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleMenuOpen}
          >
            {" "}
            Actions{" "}
          </Button>{" "}
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            {" "}
            {/* Add Regulation */}{" "}
            <MenuItem disableRipple>
              {" "}
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleOpenAdd();
                }}
              >
                {" "}
                ADD REGULATION{" "}
              </Button>{" "}
            </MenuItem>{" "}
            {/* Download CSV Format */}
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleMenuClose();
                  const link = document.createElement("a");
                  link.href = `${process.env.PUBLIC_URL}/regulations.csv`;
                  link.setAttribute("download", "regulations.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            {/* Upload CSV */}{" "}
            <MenuItem disableRipple>
              {" "}
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                {" "}
                UPLOAD CSV{" "}
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleMenuClose();
                    handleUploadCsv(e);
                  }}
                />{" "}
              </Button>{" "}
            </MenuItem>{" "}
          </Menu>
          <TextField
            size="small"
            placeholder="Search data"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                setPage(0);
                fetchData();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setPage(0);
                      fetchData();
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            autoHeight
            pageSize={pageSize} // controlled pageSize
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            pagination
          />
        </Box>
      </Paper>

      {/* Dialog for Add/Edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Regulation" : "Add Regulation"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Regulation Code (e.g., R20)"
                value={form.regulation_code}
                onChange={(e) =>
                  setForm({ ...form, regulation_code: e.target.value.toUpperCase() })
                }
                required
                fullWidth
                inputProps={{ maxLength: 10 }}
              />
              <TextField
                label="Status"
                margin="dense"
                required
                select
                fullWidth
                value={form.status}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="" disabled hidden>
                  Select Status
                </option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </TextField>
            </Stack>
            <TextField
              label="Regulation Name"
              value={form.regulation_name}
              onChange={(e) => setForm({ ...form, regulation_name: e.target.value })}
              required
              fullWidth
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              maxRows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              ml: 2,
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* ✅ Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
